import { TCString } from '@iabtcf/core'
import { inIframe, isEmbedded } from './inIframe'
import { addTcfProxy, waitForTcfInParent, mockTcfApi, getLocator, LOCATORS } from './tcfProxy'
import { ConsentData, TalpaConsentData } from '../../typings/'
import { log, YELLOW, GREY } from './log'
import { isOnTalpaDomain } from './referrer'
import { eraseCookie, getCookie } from './cookie'
import { getParameterByName } from './getParameterByName'

export const EU_CONSENT_KEY = 'euconsent-v2'
export const C_CONSENT_KEY = 'cconsent-v2'
export const CONSENTED_VENDORS_KEY = 'consentedvendors-v2'

const TCF_TIMEOUT = 1000

function getTalpaConsent(tcString: string): boolean {
  if (!tcString) return false
  try {
    const decodedConsent = TCString.decode(tcString)
    if (!decodedConsent) return false

    const hasConsent = [2, 3, 4, 7, 9].every((purpose) => decodedConsent.purposeConsents.has(purpose))
    return hasConsent
  } catch (error) {
    if (typeof window.newrelic === 'object') window.newrelic.noticeError(error)
    return false
  }
}

function clearConsentCookies(): void {
  log('clear consent cookies', YELLOW, {
    [EU_CONSENT_KEY]: getCookie(EU_CONSENT_KEY),
    [C_CONSENT_KEY]: getCookie(C_CONSENT_KEY),
    [CONSENTED_VENDORS_KEY]: getCookie(CONSENTED_VENDORS_KEY),
  })
  eraseCookie(EU_CONSENT_KEY)
  eraseCookie(C_CONSENT_KEY)
  eraseCookie(CONSENTED_VENDORS_KEY, true)

  setTimeout(() => {
    log('cookie values:', GREY, {
      [EU_CONSENT_KEY]: getCookie(EU_CONSENT_KEY),
      [C_CONSENT_KEY]: getCookie(C_CONSENT_KEY),
      [CONSENTED_VENDORS_KEY]: getCookie(CONSENTED_VENDORS_KEY),
    })
  })
}

const tcfapi = async (cmd: string): Promise<{ tcData: any; success: boolean }> =>
  new Promise((resolve) => {
    ;(window as any).__tcfapi(cmd, 2, (tcData: any, success: boolean) =>
      resolve({
        tcData,
        success,
      }),
    )
  })

function getTCData(): Promise<ConsentData | null> {
  return new Promise(async (resolve) => {
    if (typeof (window as any).__tcfapi !== 'function') {
      return resolve(null)
    }
    const timeout = setTimeout(() => {
      log(`No response from __tcfapi after ${TCF_TIMEOUT}ms`, YELLOW)
      delete (window as any).__tcfapi
      return resolve(null)
    }, TCF_TIMEOUT)
    const cmd = isOnTalpaDomain ? 'getFullTCData' : 'getTCData'

    let { tcData, success } = await tcfapi(cmd)
    log(cmd, GREY, { tcData, success })
    if (success === false && tcData === null) {
      log('Retry', YELLOW)
      ;({ tcData, success } = await tcfapi(cmd))
      log(cmd, GREY, { tcData, success })
    }

    clearTimeout(timeout)

    const { tcString, tcStringCustom } = tcData || {}
    if (tcString) {
      const euConsentCookie = getCookie(EU_CONSENT_KEY)
      // We should clear consent cookie when values are not up to date
      if (euConsentCookie && euConsentCookie !== tcString) {
        clearConsentCookies()
      }
      return resolve({
        tcString,
        tcStringCustom: tcStringCustom || '',
      })
    }
    clearConsentCookies()
    return resolve(null)
  })
}

async function getConsent(): Promise<ConsentData | null> {
  // TCF loaded
  if (typeof (window as any).__tcfapi === 'function') {
    log('tcfApi available')
    return await getTCData()
  }

  // Consent From URL
  const tcString = getParameterByName(EU_CONSENT_KEY)
  const tcStringCustom = getParameterByName(C_CONSENT_KEY)
  if (tcString) {
    const consent = {
      tcString,
      tcStringCustom: tcStringCustom || '',
    }
    log(`Found tcString in URL | ${tcString}`)
    mockTcfApi(consent)
    return consent
  }

  // Consent From Parent

  if (inIframe) {
    await waitForTcfInParent() // TCF api might not have been loaded yet, so we need to wait
  }

  const tcfFrame = getLocator(LOCATORS.TCF)
  if (tcfFrame) {
    log('Adding addTcfProxy')
    ;(window as any).TN_GDPR_BYPASS = true
    if (isOnTalpaDomain) {
      ;(window as any).ON_TALPA_DOMAIN = true
    }
    addTcfProxy(tcfFrame)
    return await getTCData()
  }

  const cmpFrame = getLocator(LOCATORS.CMP)
  if (cmpFrame) {
    log('Adding addTcfProxy')
    addTcfProxy(cmpFrame)
    const consent = await getTCData()
    ;(window as any).TN_GDPR_BYPASS = true
    if (consent) {
      log('Mock TCF API', GREY, consent)
      // the freewheel plugin is only able to use __tcfapiLocator so we need to mock it here
      mockTcfApi(consent)
      return consent
    }
  }

  if (isEmbedded) {
    clearConsentCookies()
  }
  log('no tcString found', YELLOW)
  return null
}

export async function checkConsent(): Promise<TalpaConsentData> {
  const consent = await getConsent()
  if (inIframe || isEmbedded) {
    ;(window as any).TN_GDPR_BYPASS = true
  }
  if (!consent) {
    return { tcString: '', tcStringCustom: '', hasTalpaConsent: false }
  }
  return { ...consent, hasTalpaConsent: getTalpaConsent(consent.tcString || '') }
}

export function validateTcf(): void {
  try {
    if (typeof (window as any).__tcfapi === 'undefined') {
      window.newrelic.interaction().actionText('tcf result: no __tcfapi').save()
      return
    }
    ;(window as any).__tcfapi('getTCData', 2, (tcData: any, success: boolean) => {
      log('getTCData  result', success ? GREY : YELLOW, {
        tcData,
        success,
      })
      const msg = tcData?.msg ? ` (${tcData.msg})` : ''
      window.newrelic
        .interaction()
        .actionText(`tcf result: ${success ? 'success' : 'failed'}${msg}`)
        .save()
    })
  } catch (error) {
    window.newrelic
      .interaction()
      .actionText(`tcf result: ${(error as any).message}`)
      .save()
  }
}
